import React, { useState, useEffect } from 'react';
import { ArrowLeft, Search, Filter, DollarSign, Calendar } from 'lucide-react';
import apiService from '../../services/api';

const DataStatistics = ({ onBack }) => {
  const [stats, setStats] = useState({
    total_revenue: 0,
    total_count: 0,
    pages: 1,
    current_page: 1,
    items: []
  });
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  
  // Filters
  const [page, setPage] = useState(1);
  const [serviceId, setServiceId] = useState('');
  const [userQuery, setUserQuery] = useState('');

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    fetchStats();
  }, [page, serviceId]);

  const fetchServices = async () => {
    try {
      const data = await apiService.getAdminServices();
      setServices(data);
    } catch (error) {
      console.error('Failed to fetch services', error);
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: 10,
        service_id: serviceId || undefined,
        user_query: userQuery || undefined
      };
      const data = await apiService.getSubscriptionStats(params);
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchStats();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button 
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          返回控制台
        </button>
        <h2 className="text-2xl font-bold text-gray-800">数据统计</h2>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-gray-500 text-sm font-medium">总营收</h3>
            <DollarSign className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">¥{stats.total_revenue.toFixed(2)}</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-gray-500 text-sm font-medium">总订阅数</h3>
            <Calendar className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats.total_count}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="flex items-center gap-4 w-full md:w-auto">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <select
              value={serviceId}
              onChange={(e) => { setServiceId(e.target.value); setPage(1); }}
              className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 appearance-none bg-white"
            >
              <option value="">所有服务</option>
              {services.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        </div>

        <form onSubmit={handleSearch} className="flex items-center gap-2 w-full md:w-auto">
          <div className="relative flex-grow md:flex-grow-0">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="搜索用户(昵称/邮箱)..."
              value={userQuery}
              onChange={(e) => setUserQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full md:w-64"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            查询
          </button>
        </form>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="p-4 text-sm font-medium text-gray-500">ID</th>
                <th className="p-4 text-sm font-medium text-gray-500">用户</th>
                <th className="p-4 text-sm font-medium text-gray-500">服务</th>
                <th className="p-4 text-sm font-medium text-gray-500">金额</th>
                <th className="p-4 text-sm font-medium text-gray-500">状态</th>
                <th className="p-4 text-sm font-medium text-gray-500">有效期</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan="6" className="p-8 text-center text-gray-500">加载中...</td></tr>
              ) : stats.items.length === 0 ? (
                <tr><td colSpan="6" className="p-8 text-center text-gray-500">暂无数据</td></tr>
              ) : (
                stats.items.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                    <td className="p-4 text-sm text-gray-600">#{item.id}</td>
                    <td className="p-4">
                      <div className="text-sm font-medium text-gray-900">{item.user_name}</div>
                      <div className="text-xs text-gray-500">{item.user_email}</div>
                    </td>
                    <td className="p-4 text-sm text-gray-600">{item.service_name}</td>
                    <td className="p-4 text-sm font-medium text-gray-900">¥{item.amount.toFixed(2)}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        item.is_valid ? 'bg-green-100 text-green-700' : 
                        item.status === 0 ? 'bg-gray-100 text-gray-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {item.is_valid ? '生效中' : item.status === 0 ? '已过期' : '已过期'}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-gray-500">
                      <div className="flex flex-col space-y-1">
                        <div className="text-xs">
                          <span className="text-gray-400 mr-1">起:</span>
                          {item.start_time ? new Date(item.start_time).toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                        </div>
                        <div className="text-xs">
                          <span className="text-gray-400 mr-1">止:</span>
                          {item.end_time ? new Date(item.end_time).toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {stats.pages > 1 && (
          <div className="p-4 border-t border-gray-100 flex justify-center gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
            >
              上一页
            </button>
            <span className="px-3 py-1 text-gray-600">
              {page} / {stats.pages}
            </span>
            <button
              disabled={page === stats.pages}
              onClick={() => setPage(p => Math.min(stats.pages, p + 1))}
              className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
            >
              下一页
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataStatistics;